import React from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  useTheme,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const AccessibilityPage: React.FC = () => {
  const theme = useTheme();

  const sectionHeaderSx = {
    color: theme.palette.text.primary,
    mt: 5,
    mb: 2,
    fontWeight: 600,
  };

  const bodySx = {
    color: theme.palette.text.secondary,
    mb: 2,
    lineHeight: 1.8,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit" underline="hover">
            Home
          </Link>
          <Typography color="text.primary">Accessibility</Typography>
        </Breadcrumbs>

        <Typography variant="h3" sx={{ color: theme.palette.text.primary, mb: 1, fontWeight: 700 }}>
          Accessibility Statement
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
          Last updated: March 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* Commitment */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          1. Our Commitment
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin S.A. is committed to ensuring that our platform is accessible to all users,
          including persons with disabilities. We recognise our obligations under Directive (EU)
          2016/2102 on the accessibility of websites and mobile applications of public sector bodies,
          and we apply its principles voluntarily as a private sector organisation to demonstrate our
          commitment to digital inclusion.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          This accessibility statement applies to the AmudFin web application accessible at
          amudfin.com and all sub-pages of the platform.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Conformance */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          2. Conformance Status
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin targets conformance with the Web Content Accessibility Guidelines (WCAG) 2.1
          at Level AA, as referenced by the European standard EN 301 549 V3.2.1 (2021-03), which
          is the harmonised standard for web accessibility in the European Union.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Our current conformance status is <strong>partially conformant</strong>. We are actively
          working to resolve identified accessibility barriers and improve conformance across all
          platform modules.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Accessibility Features */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          3. Accessibility Features
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The following accessibility features are implemented across the AmudFin platform:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="Semantic HTML structure"
              secondary="Pages use proper heading hierarchy, landmark regions, and meaningful HTML elements to support screen reader navigation."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Keyboard navigation"
              secondary="All interactive elements (buttons, links, form controls, menus) are accessible via keyboard. Focus indicators are visible for all focusable elements."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="ARIA labels and roles"
              secondary="Interactive components use appropriate ARIA attributes to convey state, role, and purpose to assistive technologies."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Colour contrast"
              secondary="Text and interactive elements meet WCAG 2.1 AA minimum contrast ratios (4.5:1 for normal text, 3:1 for large text)."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Responsive design"
              secondary="The platform adapts to different screen sizes and supports text resizing up to 200% without loss of content or functionality."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Form accessibility"
              secondary="All form inputs have associated labels, error messages are programmatically associated with their fields, and required fields are clearly indicated."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* Known Limitations */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          4. Known Limitations
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Despite our efforts, some areas of the platform have known accessibility limitations that
          we are working to address:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="Complex data visualisations"
              secondary="Charts and graphs (NAV trends, risk distributions, cash projections) may not be fully accessible to screen readers. We provide data table alternatives where possible and are implementing additional text descriptions for all chart components."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="PDF document generation"
              secondary="Some generated PDF reports (KID documents, compliance reports) may not yet conform to PDF/UA (ISO 14289-1) standards. We are working to ensure all generated documents are tagged PDFs with proper reading order."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Drag-and-drop interactions"
              secondary="Certain dashboard customisation features use drag-and-drop interactions that may be difficult for keyboard-only users. Alternative configuration methods are available through settings menus."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* Reporting Issues */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          5. Reporting Accessibility Issues
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          If you encounter an accessibility barrier on the AmudFin platform, we want to hear from
          you. Please contact us with the following information:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="The URL or page where you encountered the issue" />
          </ListItem>
          <ListItem>
            <ListItemText primary="A description of the accessibility barrier" />
          </ListItem>
          <ListItem>
            <ListItemText primary="The assistive technology you were using (if applicable), including name and version" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Your browser and operating system" />
          </ListItem>
        </List>
        <Typography variant="body1" sx={bodySx}>
          <strong>Accessibility contact:</strong><br />
          Email: accessibility@amudfin.com<br />
          Response time: We aim to acknowledge accessibility reports within 5 business days and
          provide a substantive response, including a remediation timeline, within 15 business days.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Enforcement */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          6. Enforcement and Complaint Procedure
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          If you are not satisfied with our response to your accessibility concern, you may contact
          the Luxembourg national enforcement body for web accessibility:
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>Service Information et Presse (SIP)</strong><br />
          Government of the Grand Duchy of Luxembourg<br />
          33, Boulevard Roosevelt, L-2450 Luxembourg<br />
          Website: https://sip.gouvernement.lu<br />
          Email: accessibilite@sip.etat.lu
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The SIP is responsible for monitoring compliance with web accessibility requirements in
          Luxembourg, in accordance with the transposition of Directive (EU) 2016/2102 into
          Luxembourg law through the Law of 28 May 2019.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Review */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          7. Review and Updates
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          This accessibility statement is reviewed and updated at least annually, or whenever
          significant changes are made to the platform. Our most recent accessibility audit was
          conducted in March 2026. We are committed to continuous improvement of our
          platform&apos;s accessibility.
        </Typography>

        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            For accessibility questions or to request content in an alternative format, contact
            accessibility@amudfin.com.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default AccessibilityPage;
